/**
 * Variation Display Helpers — single source of truth for how a product's
 * selected variation is shown to the customer (name + image).
 *
 * Used by:
 *   - BuildYourLookPage slot tiles
 *   - CartPage / CheckoutPage / CartConfirmation line items
 *   - any product card that has access to a selected variation
 *
 * IMPORTANT: every consumer must pass through these helpers (not roll its own)
 * so the customer-facing name/image stay consistent across the entire site.
 *
 * Field aliases are tolerated on BOTH the product side and the variation side
 * so legacy cart rows, BYL slots, and snapshots from older builds keep working
 * without a one-shot migration:
 *
 *   product:    { id, name, outfit_name?, outfit_image?, image?, images?[],
 *                 rental_options?[] }
 *   variation:  { id, name, image?, rental_option_id?, rental_option_name?,
 *                 rental_option_image?, selected_option_id?, selected_option_name? }
 *
 * For cart-item / BYL-slot shapes that are flat (name + rental_option_name +
 * rental_option_image at the top level), pass the same object as BOTH
 * arguments and the helpers normalise it.
 */

const _firstTruthy = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "");

/** Return the canonical product name without the variation suffix. */
export function getProductBaseName(product) {
  if (!product) return "";
  return _firstTruthy(product.outfit_name, product.name, "") || "";
}

/** Return the variation name, if any, normalised across the known aliases. */
export function getVariationName(variation) {
  if (!variation) return "";
  return (
    _firstTruthy(
      variation.rental_option_name,
      variation.selected_option_name,
      variation.option_name,
      // bare `name` is acceptable only when the variation object is the
      // actual `rental_options[]` entry (not the parent product). Callers
      // that pass the parent product as `variation` must rely on
      // rental_option_name / selected_option_name instead.
      variation._isVariationEntry ? variation.name : null,
    ) || ""
  );
}

/**
 * Return the display name for a product+variation pair.
 *
 *   getDisplayName(product, variation)
 *     - "Avni - Necklace"   when variation has a name
 *     - "Avni"              when no variation selected, or unnamed
 *
 * If you only have ONE object (e.g. a cart row with both product and
 * variation fields flattened), pass it as both arguments.
 */
export function getDisplayName(product, variation) {
  const base = getProductBaseName(product);
  const v = getVariationName(variation || product);
  if (!base) return v || "";
  return v ? `${base} - ${v}` : base;
}

/**
 * Return the best image URL for a product+variation pair.
 * Resolution order:
 *   1. variation.image  (or any alias: rental_option_image, etc.)
 *   2. resolve variation from product.rental_options[] using id/name
 *      if `variation` is just an id-or-name reference
 *   3. product.outfit_image  (cart-snapshot field)
 *   4. product.image
 *   5. product.images?.[0]
 *   6. null (caller can supply its own placeholder)
 */
export function getDisplayImage(product, variation) {
  // 1. direct image on the variation object
  const direct = _firstTruthy(
    variation?.image,
    variation?.rental_option_image,
    variation?.selected_option_image,
  );
  if (direct) return direct;

  // 2. lookup inside product.rental_options if variation is by id or name
  if (product?.rental_options && Array.isArray(product.rental_options)) {
    const refId = _firstTruthy(
      variation?.id,
      variation?.rental_option_id,
      variation?.selected_option_id,
    );
    const refName = _firstTruthy(
      variation?.name,
      variation?.rental_option_name,
      variation?.selected_option_name,
    );
    const opt =
      (refId && product.rental_options.find((o) => o.id === refId)) ||
      (refName && product.rental_options.find((o) => o.name === refName));
    if (opt?.image) return opt.image;
  }

  // 3-5. product-level fallbacks
  return _firstTruthy(
    product?.outfit_image,
    product?.image,
    product?.images?.[0],
    null,
  );
}

/**
 * Convenience: given a single flat item (cart row, BYL slot, look-summary
 * line) return { name, image }. The item is treated as BOTH product and
 * variation so all aliases are picked up.
 */
export function getDisplayItem(item) {
  if (!item) return { name: "", image: null };
  return {
    name: getDisplayName(item, item),
    image: getDisplayImage(item, item),
  };
}
